import React from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Link as ScrollLink } from "react-scroll";
import pic from "./img/homeimg.jpg";
import arrow from "./img/right-arrow.png";
import logo from "../Components/img/logo.png";
import Intro from "../Components/Intro";
import Tooltip from "../Components/Tooltip";
import AnimatedButton from "../Components/resumeButton";
import LetsTalkButton from "../Components/LetsTalkButton";



export const Home = () => {
  const main = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: main,
    offset: ["start start", "end start"],
  });

  // Add spring for smooth scroll animations
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Outro scroll animations for content
  const y = useTransform(smoothProgress, [0, 0.5], [0, 150]);
  const opacity = useTransform(smoothProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(smoothProgress, [0, 0.5], [1, 0.9]);
  const headerYRaw = useTransform(scrollYProgress, [0, 0.1], [0, -100]);
  const headerOpacityRaw = useTransform(scrollYProgress, [0, 0.1], [1, 0]);

  const headerY = useSpring(headerYRaw, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });
  const headerOpacity = useSpring(headerOpacityRaw, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const links = [
    { label: "services", to: "Services" },
    { label: "projects", to: "Projects1" },
    { label: "about", to: "About1" },
    { label: "let's talk.", to: "Let's Talk1" },
  ];

  // Intro animations
  const titleContainerVariants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.08, delayChildren: 2 },
    },
  };

  const letterVariants = {
    hidden: { y: 160, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const imageContainerVariants = {
    hidden: { clipPath: "inset(0% 0% 100% 0%)" },
    visible: {
      clipPath: "inset(0% 0% 0% 0%)",
      transition: { duration: 1, ease: [0.65, 0, 0.35, 1], delay: 2 },
    },
  };

  const imageVariants = {
    hidden: { scale: 1.2 },
    visible: {
      scale: 1,
      transition: { duration: 1.2, ease: [0.65, 0, 0.35, 1], delay: 2 },
    },
  };

  const slideUpVariants = {
    hidden: { y: "100%", opacity: 0 },
    visible: (custom) => ({
      y: "0%",
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        delay: 2.2 + custom * 0.2,
      },
    }),
  };

  const arrowVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut", delay: 2.2 },
    },
  };

  return (
    <section
      id="Home"
      ref={main}
      className="relative z-0 bg-[#fdfff8] min-h-screen md:h-screen"
    >
      <Intro />
      <div className="w-full h-screen flex flex-col items-center sticky top-0 px-4 sm:px-8 md:px-0">
        {/* Header */}
        <motion.header
          style={{ y: headerY, opacity: headerOpacity }}
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, ease: "easeOut", delay: 1.5 }}
          className=" fixed top-0 left-0 w-full z-[200] flex justify-between items-start md:items-center px-4 sm:px-8 md:px-[2.6rem] bg-transparent py-4 sm:py-6 "
        >
          {/* Logo */}{" "}
          <a href="/">
            {" "}
            <img
              src={logo}
              alt="Logo"
              className="w-12 h-12 transition duration-300"
            />{" "}
          </a>
          {/* Nav Links Container */}
          <div className="flex md:items-center">
            {" "}
            <ul className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-6">
              {" "}
              {links.map((item) => (
                <li key={item.label}>
                                    {item.label === "let's talk." ? (
                    <ScrollLink
                      to={item.to}
                      spy
                      smooth
                      offset={-88}
                      duration={500}
                      className="cursor-pointer"
                    >
                      <LetsTalkButton>{item.label}</LetsTalkButton>
                    </ScrollLink>
                  ) : (
                    <ScrollLink
                      to={item.to}
                      spy
                      smooth
                      offset={-88}
                      duration={500}
                      className="cursor-pointer px-2 py-1 rounded-xl text-black transition-colors duration-300 hover:bg-clip-text hover:text-transparent hover:bg-gradient-to-r hover:from-pink-400 hover:to-purple-500"
                    >
                      {item.label}
                    </ScrollLink>
                  )}
                </li>
              ))}{" "}
            </ul>{" "}
          </div>{" "}
        </motion.header>

        {/* Title */}
        <motion.div style={{ y, opacity }} className="w-full">
          <div className="overflow-hidden">
            <motion.h1
              initial="hidden"
              animate="visible"
              variants={titleContainerVariants}
              className="w-full text-left md:text-center font-ral font-bold tracking-tight 
             mt-[10rem] 1xs:mt-[8rem] sm:mt-[6rem] md:mb-5
             text-[11vw] md:text-[9.9vw] lg:text-[10.1vw] xl:text-[10.2vw] 2xl:text-[10.3vw]
             leading-tight md:leading-none md:whitespace-nowrap"
            >
              {["NEITHAN", "GABORNE"].map((word, wIndex) => (
                <span
                  key={wIndex}
                  className={`block md:inline-block ${
                    wIndex === 1 ? "-mt-[2vw] md:mt-0" : ""
                  } leading-[1] md:leading-none`}
                >
                  {/* Desktop-only space BEFORE the 2nd word */}
                  {wIndex === 1 && (
                    <span className="hidden md:inline">&nbsp;</span>
                  )}

                  {word.split("").map((letter, lIndex) => (
                    <motion.span
                      key={lIndex}
                      variants={letterVariants}
                      className="inline-block"
                    >
                      {letter}
                    </motion.span>
                  ))}
                </span>
              ))}
            </motion.h1>
          </div>
        </motion.div>

        {/* Content Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 items-end gap-6 md:gap-16 w-full max-w-[90rem] 4xl:max-w-[112rem]">
          {/* Left Column */}
          <motion.div
            style={{ y, opacity }}
            className="col-span-2 md:col-span-1 space-y-6 flex flex-col items-start text-left 
    max-w-full sm:max-w-[70vw] md:max-w-[50vw] lg:max-w-[40vw] xl:max-w-[32vw] 2xl:max-w-[60vw] 
    md:mx-[3vw] xl:mx-10 2xl:mx-0 3xl:mx-12 4xl:mx-0 
    xl:mb-6 2xl:mb-5 3xl:mb-20"
          >
            <motion.div
              initial="hidden"
              animate="visible"
              variants={arrowVariants}
              className="w-7 h-7 mb-6 bg-neutral-500 hidden md:block"
              style={{
                WebkitMaskImage: `url(${arrow})`,
                WebkitMaskRepeat: "no-repeat",
                WebkitMaskSize: "contain",
                WebkitMaskPosition: "center",
              }}
            ></motion.div>

            <div className="overflow-hidden">
              <motion.p
                initial="hidden"
                animate="visible"
                custom={0}
                variants={slideUpVariants}
                className="font-ral font-regular text-sm sm:text-base md:text-[0.9rem] lg:text-[1.1rem] xl:text-[1.3rem] 2xl:text-[1.5rem] text-neutral-700"
              >
                A{" "}
                <span className="font-bold">
                  passionate full stack developer
                </span>{" "}
                with a knack for crafting seamless, user-friendly digital
                experiences. Explore my work and see how I can bring your vision
                to life.
              </motion.p>
            </div>
            <div>
            <Tooltip content="View Resume">
              <motion.div
                initial="hidden"
                animate="visible"
                custom={1}
                variants={slideUpVariants}
              >
                <AnimatedButton
                  as="a"
                  href="/resume.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  RESUME
                </AnimatedButton>
              </motion.div>
            </Tooltip>
            </div>
          </motion.div>

          {/* Center Column */}
          <motion.div
            style={{ y, opacity, scale }}
            className="flex justify-start md:justify-center"
          >
            <motion.div
              initial="hidden"
              animate="visible"
              variants={imageContainerVariants}
              className="overflow-hidden"
            >
              <motion.img
                variants={imageVariants}
                src={pic}
                alt="Portrait"
                className="h-[200px] md:h-[330px] xl:h-[300px] 2xl:h-[380px] 3xl:h-[400px] 4xl:h-[600px]
                xl:mb-16 2xl:mb-0 
                object-contain drop-shadow-lg rounded-lg "
              />
            </motion.div>
          </motion.div>

          {/* Right Column */}
          <motion.div
            style={{ y, opacity }}
            className="
    flex flex-col items-end text-right space-y-4
    ml-auto mr-0 sm:mr-4
    mt-0
    max-w-[90%] sm:max-w-sm md:max-w-md xl:max-w-xl 2xl:max-w-2xl 3xl:max-w-3xl 4xl:max-w-4xl
    md:mx-[2rem] xl:mx-[3rem] 2xl:mx-0
    self-end
    xl:mb-6 2xl:mb-0 3xl:mb-12
  "
          >
            <div className="overflow-hidden">
              <motion.p
                initial="hidden"
                animate="visible"
                custom={2}
                variants={slideUpVariants}
                className="
        font-inc font-medium 
        text-sm sm:text-base lg:text-xl xl:text-2xl 2xl:text-3xl
        text-neutral-700
      "
              >
                LOOKING FOR A DEV?
              </motion.p>
            </div>
            <div className="overflow-hidden">
              <motion.p
                initial="hidden"
                animate="visible"
                custom={3}
                variants={slideUpVariants}
                className="
        font-ral font-bold 
        text-5xl md:text-6xl lg:text-7xl 2xl:text-8xl
        text-neutral-700
      "
              >
                LET'S TALK.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
